import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Eye, Save, RotateCcw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import LoadingSpinner from './LoadingSpinner';

const FollowupEmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const { toast } = useToast();

  const [settings, setSettings] = useState({
    subject: '',
    opening: '',
    closing: '',
    delayHours: 24
  });

  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/practice/followup-email-settings`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        setOriginalSettings(data.settings);
      } else {
        throw new Error('Failed to load settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: "Error",
        description: "Failed to load follow-up email settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/practice/followup-email-settings`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(settings)
        }
      );

      if (response.ok) {
        setOriginalSettings(settings);
        toast({
          title: "Success",
          description: "Follow-up email settings saved successfully"
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save follow-up email settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/practice/followup-email-preview`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(settings)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html_content);
        setShowPreview(true);
      } else {
        throw new Error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Error",
        description: "Failed to generate email preview",
        variant: "destructive"
      });
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Post Treatment Custom Message</CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Customize the automated email sent to patients after their procedure.
            <br />
            <strong className="text-blue-600">💡 Tip:</strong> Type <code className="bg-gray-100 px-2 py-1 rounded text-blue-700">{'{procedureName}'}</code> anywhere in your message, and it will automatically be replaced with the actual procedure name (like "Root Canal" or "Tooth Extraction") when the email is sent.
            <br />
            <strong className="text-green-600">Note:</strong> These custom messages only appear in the automated follow-up emails sent to patients.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Scheduling Delay */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ⏰ Send Follow-Up Email After
            </label>
            <select
              value={settings.delayHours}
              onChange={(e) => setSettings({ ...settings, delayHours: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={24}>24 hours (1 day)</option>
              <option value={48}>48 hours (2 days)</option>
              <option value={72}>72 hours (3 days)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Choose when to automatically send the follow-up email after marking a procedure as "delivered"
            </p>
          </div>

          {/* Email Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Subject Line
            </label>
            <Input
              value={settings.subject}
              onChange={(e) => setSettings({ ...settings, subject: e.target.value })}
              placeholder="Just Checking In – How Are You Feeling After Your Visit?"
            />
            <p className="text-xs text-gray-500 mt-1">
              This appears in the patient's inbox as the email subject.
            </p>
          </div>

          {/* Opening Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opening Message
            </label>
            <Textarea
              value={settings.opening}
              onChange={(e) => setSettings({ ...settings, opening: e.target.value })}
              placeholder="I hope this message finds you well. I wanted to follow up regarding the {procedureName} you had in our office yesterday..."
              rows={4}
            />
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-900 font-medium mb-1">
                ✏️ Example of how <code className="bg-blue-100 px-1 rounded">{'{procedureName}'}</code> works:
              </p>
              <p className="text-xs text-blue-800 mb-1">
                <strong>You type:</strong> "I wanted to check on your <code className="bg-blue-100 px-1 rounded">{'{procedureName}'}</code>"
              </p>
              <p className="text-xs text-blue-800">
                <strong>Patient sees:</strong> "I wanted to check on your <strong>Root Canal</strong>"
              </p>
            </div>
          </div>

          {/* Closing Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Closing Message
            </label>
            <Textarea
              value={settings.closing}
              onChange={(e) => setSettings({ ...settings, closing: e.target.value })}
              placeholder="Thank you for allowing us to care for you. Wishing you a smooth and comfortable recovery."
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              The final message before your signature. Provides a warm closing to the email.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <LoadingSpinner className="h-4 w-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
            <Button
              onClick={handlePreview}
              variant="outline"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview Email
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-900 font-medium mb-2">
              📧 What stays automatic:
            </p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
              <li>Patient name and greeting</li>
              <li>Practice logo, name, phone, and contact information</li>
              <li>"How to Reach Us" section with your phone number</li>
              <li>Your signature with dentist name</li>
              <li>Email footer with disclaimer</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Email Preview</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                Close
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gray-50 p-4 rounded mb-4">
                <p className="text-sm text-gray-600"><strong>Subject:</strong> {settings.subject}</p>
              </div>
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowupEmailSettings;
